import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, EyeOff, Trash2, Loader2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const FILTERS = [
  { key: "", label: "Всички" },
  { key: "pending", label: "Чакащи" },
  { key: "approved", label: "Одобрени" },
  { key: "hidden", label: "Скрити" },
];

const STATUS_BADGE = {
  approved: "bg-green-50 text-green-700",
  pending: "bg-amber-50 text-amber-700",
  hidden: "bg-slate-100 text-slate-500",
};

const STATUS_LABEL = { approved: "Одобрен", pending: "Чака", hidden: "Скрит" };

export default function CommentsManager() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("");

  const { data: comments, isLoading } = useQuery({
    queryKey: ["admin-comments", filter],
    queryFn: () => api.get(`/admin/comments${filter ? `?status=${filter}` : ""}`).then((r) => r.data),
  });

  const list = comments || [];

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-comments"] });
    qc.invalidateQueries({ queryKey: ["comments"] });
    qc.invalidateQueries({ queryKey: ["admin-stats"] });
  };

  const setStatus = async (c, status) => {
    try {
      await api.put(`/admin/comments/${c.id}`, { status });
      toast.success(status === "approved" ? "Коментарът е одобрен." : "Коментарът е скрит.");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (c) => {
    if (!window.confirm("Изтриване на коментара?")) return;
    try {
      await api.delete(`/admin/comments/${c.id}`);
      toast.success("Коментарът е изтрит.");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="mt-6" data-testid="comments-manager">
      <h2 className="mb-4 font-display text-lg font-bold text-slate-900">Коментари</h2>

      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key || "all"}
            onClick={() => setFilter(f.key)}
            data-testid={`comment-filter-${f.key || "all"}`}
            className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${
              filter === f.key ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
        </div>
      ) : list.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-12 text-center text-slate-500">
          Няма коментари в тази категория.
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-comment-${c.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display text-sm font-bold text-slate-900">{c.name}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_BADGE[c.status] || "bg-slate-100 text-slate-500"}`}>
                      {STATUS_LABEL[c.status] || c.status}
                    </span>
                    <span className="text-xs text-slate-400">{formatDateTime(c.created_at)}</span>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{c.content}</p>
                  <Link
                    to={`/article/${c.article_slug}`}
                    target="_blank"
                    className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    {c.article_title}
                  </Link>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {c.status !== "approved" && (
                    <button
                      onClick={() => setStatus(c, "approved")}
                      data-testid={`comment-approve-${c.id}`}
                      className="rounded-lg p-2 text-slate-500 hover:bg-green-50 hover:text-green-600"
                      aria-label="Одобри"
                      title="Одобри"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  )}
                  {c.status !== "hidden" && (
                    <button
                      onClick={() => setStatus(c, "hidden")}
                      data-testid={`comment-hide-${c.id}`}
                      className="rounded-lg p-2 text-slate-500 hover:bg-amber-50 hover:text-amber-600"
                      aria-label="Скрий"
                      title="Скрий"
                    >
                      <EyeOff className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => del(c)}
                    data-testid={`comment-delete-${c.id}`}
                    className="rounded-lg p-2 text-slate-500 hover:bg-red-50 hover:text-red-600"
                    aria-label="Изтрий"
                    title="Изтрий"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
